/* Lace theme for Shoe Lace Supply. Vanilla JS, no dependencies. */
(() => {
  'use strict';

  const SLS = window.SLS || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const isCartPage = document.body.classList.contains('template-cart');

  const handleize = (s) =>
    String(s || '').toLowerCase().replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const isColorOption = (name) => {
    const n = String(name || '').toLowerCase();
    return n.includes('color') || n.includes('colour') || n === 'style' || n === 'pattern';
  };
  const isLengthOption = (name) => {
    const n = String(name || '').toLowerCase();
    return n.includes('length') || n === 'size';
  };

  const sized = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? 'https:' + src : src;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  };

  function formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;
    const fmt = SLS.moneyFormat || '${{amount}}';
    const withDelims = (n, dec, thou, sep) => {
      const parts = n.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.join(sep);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  }

  /* ----------------------------------------------------------------------
     Layers: drawers, modal, scrim
     ---------------------------------------------------------------------- */
  const scrim = $('[data-scrim]');
  const openLayers = new Set();
  let lastFocus = null;

  function openLayer(el, focusTarget) {
    if (!el) return;
    lastFocus = document.activeElement;
    openLayers.add(el);
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    scrim.hidden = false;
    requestAnimationFrame(() => {
      if (!openLayers.has(el)) return; // closed again before the first frame
      el.classList.add('is-open');
      scrim.classList.add('is-on');
    });
    document.body.classList.add('is-locked');
    const f = focusTarget || el.querySelector('button, a, input');
    if (f) setTimeout(() => f.focus({ preventScroll: true }), 60);
  }

  function closeLayer(el) {
    if (!el || !openLayers.has(el)) return;
    openLayers.delete(el);
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (el.matches('[data-quick-modal]')) setTimeout(() => { if (!openLayers.has(el)) el.hidden = true; }, 300);
    if (openLayers.size === 0) {
      scrim.classList.remove('is-on');
      setTimeout(() => { if (openLayers.size === 0) scrim.hidden = true; }, 300);
      document.body.classList.remove('is-locked');
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }
  }

  function closeTopLayer() {
    const layers = Array.from(openLayers);
    if (layers.length) closeLayer(layers[layers.length - 1]);
  }

  scrim && scrim.addEventListener('click', () => Array.from(openLayers).forEach(closeLayer));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (QuickAdd.isDrawerViewOpen()) { QuickAdd.close(); return; }
      closeTopLayer();
      $$('details.fchip[open]').forEach((d) => d.removeAttribute('open'));
    }
  });

  /* ----------------------------------------------------------------------
     Offer state: one JSON blob from Liquid drives every meter on the page
     ---------------------------------------------------------------------- */
  const Offer = {
    state: null,
    read(root = document) {
      const node = $('[data-offer-state]', root);
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (e) { return null; }
    },
    apply(state) {
      if (!state) return;
      const prev = this.state;
      this.state = state;

      $$('[data-offer-text]').forEach((el) => {
        if (el.closest('[data-cart-drawer]')) return;
        if (el.textContent.trim() === state.message) return;
        el.style.opacity = '0';
        setTimeout(() => { el.textContent = state.message; el.style.opacity = ''; }, 160);
      });

      $$('[data-offer-dots]').forEach((wrap) => {
        $$('i', wrap).forEach((dot, i) => dot.classList.toggle('is-on', i < state.filled));
      });

      $$('[data-cart-count]').forEach((el) => {
        el.textContent = state.itemCount;
        el.hidden = state.itemCount === 0;
        if (prev && prev.itemCount !== state.itemCount) {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        }
      });

      document.dispatchEvent(new CustomEvent('sls:offer', { detail: state }));
    }
  };

  /* ----------------------------------------------------------------------
     Cart API with Section Rendering
     ---------------------------------------------------------------------- */
  const drawer = $('[data-cart-drawer]');
  const SECTIONS = ['cart-drawer', 'offer-status'];

  const Cart = {
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong.');
      return data;
    },
    async add(items) {
      const data = await this.request(SLS.routes.cartAdd, { items });
      if (isCartPage) { window.location.reload(); return data; }
      this.render(data.sections);
      return data;
    },
    async change(line, quantity) {
      const body = drawer && $('[data-cart-body]', drawer);
      body && body.classList.add('is-busy');
      try {
        const data = await this.request(SLS.routes.cartChange, { line, quantity });
        if (isCartPage) { window.location.reload(); return; }
        this.render(data.sections);
      } finally {
        body && body.classList.remove('is-busy');
      }
    },
    async refresh() {
      const res = await fetch(`${SLS.routes.root}?sections=${SECTIONS.join(',')}`);
      if (res.ok) this.render(await res.json());
    },
    render(sections) {
      if (!sections) return;
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      if (sections['cart-drawer'] && drawer) {
        const fresh = $('[data-cart-body]', parse(sections['cart-drawer']));
        const current = $('[data-cart-body]', drawer);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
      if (sections['offer-status']) {
        Offer.apply(Offer.read(parse(sections['offer-status'])));
      }
    },
    open() { openLayer(drawer, $('[data-cart-close]', drawer)); },
    close() { if (QuickAdd.isDrawerViewOpen()) QuickAdd.close(); closeLayer(drawer); }
  };

  window.SLS.cart = Cart;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && !isCartPage && drawer) { e.preventDefault(); Cart.open(); return; }
    if (e.target.closest('[data-cart-close]')) { Cart.close(); return; }

    const qtyBtn = e.target.closest('[data-line-qty]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-line]');
      if (!line) return;
      e.preventDefault();
      Cart.change(Number(line.dataset.line), Math.max(0, Number(qtyBtn.dataset.lineQty))).catch(console.error);
    }
  });

  // Keep drawer and meters fresh if the cart changed in another tab or via an app.
  window.addEventListener('pageshow', (e) => { if (e.persisted) Cart.refresh(); });

  /* ----------------------------------------------------------------------
     Swatch helper for JS-built pickers (Quick Add)
     ---------------------------------------------------------------------- */
  function swatchEl(name, imgSrc) {
    const span = document.createElement('span');
    const base = handleize(String(name).split(' (')[0]);
    const key = handleize(name);
    span.className = `sw sw--${base}${key !== base ? ' sw--' + key : ''}`;
    if (imgSrc) span.style.setProperty('--sw-img', `url("${sized(imgSrc, 200)}") center/260% no-repeat`);
    span.setAttribute('aria-hidden', 'true');
    return span;
  }

  /* ----------------------------------------------------------------------
     Quick Add modal
     ---------------------------------------------------------------------- */
  const modal = $('[data-quick-modal]');
  const pview = $('[data-pview]'); // product view that slides in over the cart drawer
  const QuickAdd = {
    root: modal,
    inDrawer: false,
    product: null,
    selected: [],
    free: false,
    cache: new Map(),

    async fetchProduct(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      const res = await fetch(`${SLS.routes.root}products/${handle}.js`);
      if (!res.ok) throw new Error('Product not found');
      const p = await res.json();
      this.cache.set(handle, p);
      return p;
    },

    async open(handle, opts = {}) {
      const inDrawer = !!(opts.inDrawer && pview && drawer && openLayers.has(drawer));
      if (!inDrawer && !modal) return;
      let p;
      try { p = await this.fetchProduct(handle); } catch (e) { window.location.href = `${SLS.routes.root}products/${handle}`; return; }
      this.product = p;
      this.free = !!opts.free;
      const first = p.variants.find((v) => v.available) || p.variants[0];
      this.selected = [...first.options];
      if (opts.color) {
        const ci = p.options.findIndex((o) => isColorOption(o.name));
        if (ci > -1 && p.options[ci].values.includes(opts.color)) {
          this.selected[ci] = opts.color;
          const match = this.findVariant() || p.variants.find((v) => v.options[ci] === opts.color && v.available);
          if (match) this.selected = [...match.options];
        }
      }
      this.inDrawer = inDrawer;
      this.root = inDrawer ? pview : modal;
      this.build();
      this.update();
      if (inDrawer) {
        pview.hidden = false;
        pview.setAttribute('aria-hidden', 'false');
        const scroller = $('.pview__scroll', pview);
        if (scroller) scroller.scrollTop = 0;
        requestAnimationFrame(() => pview.classList.add('is-open'));
        const back = $('[data-qa-back]', pview);
        if (back) setTimeout(() => back.focus({ preventScroll: true }), 60);
      } else {
        openLayer(modal, $('[data-qa-submit]', modal));
      }
    },

    close() {
      if (this.inDrawer && pview) {
        pview.classList.remove('is-open');
        pview.setAttribute('aria-hidden', 'true');
        setTimeout(() => { if (!pview.classList.contains('is-open')) pview.hidden = true; }, 320);
        this.inDrawer = false;
      } else if (modal) {
        closeLayer(modal);
      }
    },

    isDrawerViewOpen() {
      return !!(pview && pview.classList.contains('is-open'));
    },

    findVariant() {
      return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i]));
    },

    imageForColor(optIndex, value) {
      const v = this.product.variants.find((x) => x.options[optIndex] === value && x.featured_image);
      return v ? v.featured_image.src : null;
    },

    build() {
      const p = this.product;
      $('[data-qa-title]', this.root).textContent = p.title;
      $('[data-qa-link]', this.root).href = p.url;
      $('[data-qa-badge]', this.root).hidden = !this.free;
      const wrap = $('[data-qa-options]', this.root);
      wrap.innerHTML = '';
      const hasOnlyDefault = p.variants.length === 1 && p.variants[0].title === 'Default Title';
      if (hasOnlyDefault) return;

      p.options.forEach((opt, i) => {
        const color = isColorOption(opt.name);
        const label = String(opt.name).split('(').pop().replace(')', '');
        const fs = document.createElement('fieldset');
        fs.className = 'opt' + (color ? ' opt--color' : '');
        fs.innerHTML = `<legend class="opt__legend"><span class="opt__name">${label}:</span> <span class="opt__value" data-qa-value="${i}"></span>${
          isLengthOption(opt.name) && SLS.sizeGuide ? `<a class="opt__help" href="${SLS.sizeGuide}" target="_blank" rel="noopener">Find Your Length</a>` : ''
        }</legend>`;
        const values = document.createElement('div');
        values.className = 'opt__values' + (color ? ' opt__values--sw' : '');
        opt.values.forEach((val) => {
          const lab = document.createElement('label');
          lab.className = color ? 'sw-opt' : 'pill';
          lab.title = val;
          const input = document.createElement('input');
          input.type = 'radio';
          input.className = 'vh';
          input.name = `qa-opt-${i}`;
          input.value = val;
          input.dataset.index = i;
          lab.appendChild(input);
          if (color) {
            lab.appendChild(swatchEl(val, this.imageForColor(i, val)));
            const t = document.createElement('span'); t.className = 'vh'; t.textContent = val; lab.appendChild(t);
          } else {
            const t = document.createElement('span'); t.textContent = val; lab.appendChild(t);
          }
          values.appendChild(lab);
        });
        fs.appendChild(values);
        wrap.appendChild(fs);
      });
    },

    update() {
      const p = this.product;
      const v = this.findVariant();
      $$('input[type=radio]', this.root).forEach((input) => {
        const i = Number(input.dataset.index);
        input.checked = this.selected[i] === input.value;
        const test = [...this.selected]; test[i] = input.value;
        const avail = p.variants.some((x) => x.available && x.options.every((o, k) => o === test[k]));
        input.parentElement.classList.toggle('is-soldout', !avail);
      });
      $$('[data-qa-value]', this.root).forEach((el) => { el.textContent = this.selected[Number(el.dataset.qaValue)]; });

      const img = $('[data-qa-img]', this.root);
      const src = (v && v.featured_image && v.featured_image.src) || p.featured_image;
      if (src) { img.src = sized(src, 800); img.alt = p.title; }

      $('[data-qa-price]', this.root).textContent = v ? formatMoney(v.price) : '';
      const btn = $('[data-qa-submit]', this.root);
      btn.disabled = !v || !v.available;
      btn.textContent = !v ? 'Unavailable' : !v.available ? 'Sold Out' : this.free ? 'Add My Free Pair' : 'Add To Cart';
      $('[data-qa-link]', this.root).href = v ? `${p.url}?variant=${v.id}` : p.url;
    },

    async submit() {
      const v = this.findVariant();
      if (!v) return;
      const btn = $('[data-qa-submit]', this.root);
      btn.classList.add('is-loading');
      try {
        const wasInDrawer = this.inDrawer;
        await Cart.add([{ id: v.id, quantity: 1 }]);
        this.close();
        if (!wasInDrawer && !isCartPage) setTimeout(() => Cart.open(), openLayers.has(drawer) ? 0 : 200);
      } catch (err) {
        btn.textContent = err.message;
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  };

  [modal, pview].filter(Boolean).forEach((root) => {
    root.addEventListener('change', (e) => {
      const input = e.target.closest('input[type=radio]');
      if (!input || QuickAdd.root !== root) return;
      QuickAdd.selected[Number(input.dataset.index)] = input.value;
      if (!QuickAdd.findVariant()) {
        // Snap to the closest available combo that keeps this choice.
        const i = Number(input.dataset.index);
        const alt = QuickAdd.product.variants.find((v) => v.available && v.options[i] === input.value)
          || QuickAdd.product.variants.find((v) => v.options[i] === input.value);
        if (alt) QuickAdd.selected = [...alt.options];
      }
      QuickAdd.update();
    });
    const submit = $('[data-qa-submit]', root);
    if (submit) submit.addEventListener('click', () => QuickAdd.submit());
  });
  if (modal) $('[data-quick-close]', modal).addEventListener('click', () => closeLayer(modal));
  if (pview) $('[data-qa-back]', pview).addEventListener('click', () => QuickAdd.close());

  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-add]');
    if (!trigger) return;
    e.preventDefault();
    const card = trigger.closest('[data-card]');
    if (trigger.dataset.variantId) {
      trigger.classList.add('is-loading');
      try {
        await Cart.add([{ id: Number(trigger.dataset.variantId), quantity: 1 }]);
        if (!isCartPage) Cart.open();
      } catch (err) { console.error(err); }
      trigger.classList.remove('is-loading');
      return;
    }
    const active = card && $('.sw-btn.is-active', card);
    QuickAdd.open(trigger.dataset.quickAdd, {
      free: trigger.dataset.free === 'true',
      color: active ? active.dataset.name : null,
      inDrawer: trigger.dataset.inDrawer === 'true'
    });
  });

  /* ----------------------------------------------------------------------
     Product cards: swatch preview
     ---------------------------------------------------------------------- */
  function selectCardSwatch(btn) {
    const card = btn.closest('[data-card]');
    if (!card) return;
    $$('.sw-btn', card).forEach((b) => {
      const on = b === btn;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    const img = $('.card__img:not(.card__img--alt)', card);
    if (img && btn.dataset.img) {
      img.removeAttribute('srcset');
      img.src = btn.dataset.img;
      card.classList.add('is-swatched');
    }
    if (btn.dataset.url) $$('[data-card-link]', card).forEach((a) => { a.href = btn.dataset.url; });
    const label = $('[data-card-color]', card);
    if (label) label.textContent = btn.dataset.name;
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-card-swatch]');
    if (btn) { e.preventDefault(); selectCardSwatch(btn); }
  });
  if (window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('mouseover', (e) => {
      const btn = e.target.closest('[data-card-swatch]');
      if (btn && !btn.classList.contains('is-active')) selectCardSwatch(btn);
    });
  }

  /* ----------------------------------------------------------------------
     Product page
     ---------------------------------------------------------------------- */
  function initProduct(root) {
    const json = $('[data-product-json]', root);
    if (!json) return;
    const product = JSON.parse(json.textContent);
    const form = $('[data-product-form]', root);
    const idInput = $('[data-variant-id]', root);
    const qtyInput = $('[data-qty]', root);
    const addBtn = $('[data-add-btn]', root);
    const addLabel = $('[data-add-label]', root);
    const addTotal = $('[data-add-total]', root);
    const errorEl = $('[data-add-error]', root);
    const track = $('[data-gallery-track]', root);
    const thumbs = $$('[data-thumb]', root);
    const sticky = $('[data-sticky-buy]');
    const group = Number(root.dataset.group) || 3;
    const buy = Number(root.dataset.buy) || 2;
    const eligible = root.dataset.eligible === 'true';

    /* Mix and match: one slot per pair when a 3 or 6 pack is chosen */
    const mixWrap = $('[data-mix]', root);
    const slotsEl = $('[data-mix-slots]', root);
    const sameBtn = $('[data-mix-same]', root);
    const lenBtn = $('[data-mix-len]', root);
    const packHint = $('[data-pack-hint]', root);
    const optSets = () => $$('[data-opt]', root);
    const colorIndex = optSets().findIndex((fs) => fs.classList.contains('opt--color'));
    const lengthIndex = optSets().findIndex((fs, i) => i !== colorIndex);
    let mix = [];
    let active = 0;
    const mixOn = () => mix.length > 1;

    const variantFor = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    const closestVariant = (opts, keepIndex) =>
      variantFor(opts)
      || product.variants.find((v) => v.available && v.options[keepIndex] === opts[keepIndex])
      || product.variants.find((v) => v.options[keepIndex] === opts[keepIndex]);

    const selected = () => $$('[data-opt]', root).map((fs) => {
      const checked = $('input:checked', fs);
      return checked ? checked.value : null;
    });

    let variant = product.variants.find((v) => String(v.id) === String(idInput && idInput.value)) || product.variants[0];

    const payFor = (q) => (eligible ? q - Math.floor(q / group) * (group - buy) : q);

    function renderTotal() {
      if (!addTotal || !variant) return;
      const q = mixOn() ? mix.length : Math.max(1, Number(qtyInput ? qtyInput.value : 1));
      const prices = mixOn() ? mix.map((v) => v.price) : Array(q).fill(variant.price);
      const full = prices.reduce((a, b) => a + b, 0);
      const freeCount = q - payFor(q);
      const now = full - [...prices].sort((a, b) => a - b).slice(0, freeCount).reduce((a, b) => a + b, 0);
      const pay = q - freeCount;
      addTotal.innerHTML = pay < q
        ? `<s>${formatMoney(full)}</s>${formatMoney(now)}`
        : formatMoney(now);
      if (addLabel && variant.available) addLabel.textContent = q > 1 ? `Add ${q} Pairs` : 'Add To Cart';
    }

    function renderPacks() {
      $$('[data-pack]', root).forEach((input) => {
        const pack = input.closest('.pack');
        if (!pack) return;
        const price = $('[data-pack-price]', pack);
        const was = $('[data-pack-was]', pack);
        if (price) price.textContent = formatMoney(variant.price * Number(input.dataset.pay));
        if (was) was.textContent = formatMoney(variant.price * Number(input.value));
      });
    }

    function scrollToMedia(mediaId) {
      if (!track || !mediaId) return;
      const slides = $$('.gallery__slide', track);
      const idx = slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (idx > -1) track.scrollTo({ left: slides[idx].offsetLeft, behavior: 'smooth' });
    }

    function updateAvailability() {
      const sel = selected();
      $$('[data-opt]', root).forEach((fs, i) => {
        $$('input', fs).forEach((input) => {
          const test = [...sel]; test[i] = input.value;
          const ok = product.variants.some((v) => v.available && v.options.every((o, k) => o === test[k]));
          input.parentElement.classList.toggle('is-soldout', !ok);
        });
      });
    }

    function syncPicker(v) {
      if (!v) return;
      optSets().forEach((fs, i) => {
        const input = $$('input', fs).find((inp) => inp.value === v.options[i]);
        if (input) input.checked = true;
      });
      onVariantChange();
    }

    function renderSlots() {
      if (!mixWrap) return;
      mixWrap.hidden = !mixOn();
      if (packHint) packHint.hidden = mixOn();
      if (!mixOn()) return;
      slotsEl.innerHTML = '';
      mix.forEach((v, i) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mix__slot' + (i === active ? ' is-active' : '') + (v && !v.available ? ' is-soldout' : '');
        btn.dataset.slot = i;
        const isFree = eligible && (i + 1) % group === 0;
        const color = v && colorIndex > -1 ? v.options[colorIndex] : '';
        const length = v && lengthIndex > -1 ? v.options[lengthIndex] : '';
        const img = v && v.featured_image ? (v.featured_image.src || v.featured_image) : null;
        const dot = swatchEl(color || (v ? v.title : ''), img);
        btn.appendChild(dot);
        const text = document.createElement('span');
        text.className = 'mix__text';
        text.innerHTML = `<span class="mix__label">${isFree ? 'Free Pair' : 'Pair ' + (i + 1)}</span><span class="mix__value"></span>`;
        text.querySelector('.mix__value').textContent = [color, length].filter(Boolean).join(' · ') + (v && !v.available ? ' (Sold Out)' : '');
        btn.appendChild(text);
        btn.setAttribute('aria-pressed', i === active ? 'true' : 'false');
        btn.setAttribute('aria-label', `${isFree ? 'Free pair' : 'Pair ' + (i + 1)}: ${color} ${length}. Tap to change.`);
        li.appendChild(btn);
        slotsEl.appendChild(li);
      });
      const colors = new Set(mix.map((v) => v && v.options[colorIndex]));
      const lengths = new Set(mix.map((v) => v && v.options[lengthIndex]));
      if (sameBtn) sameBtn.hidden = colorIndex < 0 || colors.size === 1;
      if (lenBtn) lenBtn.hidden = lengthIndex < 0 || lengths.size === 1;
      const allOk = mix.every((v) => v && v.available);
      addBtn.disabled = !allOk;
      if (!allOk) addLabel.textContent = 'Pick An Available Option';
      else renderTotal();
    }

    function setPack(n) {
      n = Math.max(1, Number(n) || 1);
      if (qtyInput) qtyInput.value = n;
      if (n > 1) {
        const prevLen = mix.length;
        const next = [];
        for (let i = 0; i < n; i++) next.push(mix[i] || variant);
        mix = next;
        // Pair 1 keeps what they already picked; the next empty pair is ready for a new color.
        if (n > prevLen) active = prevLen > 1 ? prevLen : 1;
        if (active >= n) active = n - 1;
        if (mix.length && mix[active] !== variant) syncPicker(mix[active]);
      } else {
        mix = [];
        active = 0;
      }
      renderSlots();
      renderTotal();
    }

    if (slotsEl) {
      slotsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-slot]');
        if (!btn) return;
        active = Number(btn.dataset.slot);
        syncPicker(mix[active]);
        renderSlots();
      });
    }
    sameBtn && sameBtn.addEventListener('click', () => {
      const c = mix[active].options[colorIndex];
      mix = mix.map((v) => { const o = [...v.options]; o[colorIndex] = c; return closestVariant(o, colorIndex) || v; });
      renderSlots();
    });
    lenBtn && lenBtn.addEventListener('click', () => {
      const l = mix[active].options[lengthIndex];
      mix = mix.map((v) => { const o = [...v.options]; o[lengthIndex] = l; return variantFor(o) || v; });
      renderSlots();
    });

    function onVariantChange() {
      let sel = selected();
      let v = product.variants.find((x) => x.options.every((o, i) => o === sel[i]));
      if (!v) {
        // Keep the option they just picked, move the others to something that exists.
        const changed = document.activeElement && document.activeElement.closest('[data-opt]');
        const ci = changed ? $$('[data-opt]', root).indexOf(changed) : 0;
        v = product.variants.find((x) => x.available && x.options[ci] === sel[ci]) || product.variants.find((x) => x.options[ci] === sel[ci]);
        if (v) {
          $$('[data-opt]', root).forEach((fs, i) => {
            const input = $$('input', fs).find((inp) => inp.value === v.options[i]);
            if (input) input.checked = true;
          });
          sel = v.options;
        }
      }
      $$('[data-opt]', root).forEach((fs, i) => {
        const label = $('[data-opt-value]', fs);
        if (label) label.textContent = sel[i] || '';
      });
      updateAvailability();
      if (!v) {
        addBtn.disabled = true;
        addLabel.textContent = 'Unavailable';
        return;
      }
      variant = v;
      idInput.value = v.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());

      const priceNow = $('[data-price-now]', root);
      if (priceNow) priceNow.textContent = formatMoney(v.price);
      const priceWrap = $('[data-price]', root);
      let was = $('[data-price-was]', root);
      if (v.compare_at_price > v.price) {
        if (!was && priceWrap) { was = document.createElement('s'); was.className = 'price__was'; was.dataset.priceWas = ''; priceWrap.appendChild(was); }
        if (was) was.textContent = formatMoney(v.compare_at_price);
        priceWrap && priceWrap.classList.add('price--sale');
      } else {
        if (was) was.remove();
        priceWrap && priceWrap.classList.remove('price--sale');
      }

      addBtn.disabled = !v.available;
      addLabel.textContent = v.available ? 'Add To Cart' : 'Sold Out';
      if (v.featured_media) scrollToMedia(v.featured_media.id);
      const sv = $('[data-sticky-variant]');
      if (sv) sv.textContent = v.title;
      const sa = $('[data-sticky-add]');
      if (sa) sa.disabled = !v.available;
      renderPacks();
      renderTotal();
    }

    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-opt-input]')) {
        onVariantChange();
        if (mixOn()) {
          mix[active] = variant;
          // Picking a color fills this pair and moves on to the next one.
          const pickedColor = e.target.closest('[data-opt]') === optSets()[colorIndex];
          if (pickedColor && active < mix.length - 1) {
            active += 1;
            syncPicker(mix[active]);
          }
          renderSlots();
        }
      }
      if (e.target.matches('[data-pack]')) setPack(e.target.value);
      if (e.target.matches('[data-qty]')) renderTotal();
    });

    root.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (step && qtyInput) {
        qtyInput.value = Math.max(1, Number(qtyInput.value || 1) + Number(step.dataset.qtyStep));
        renderTotal();
      }
      const thumb = e.target.closest('[data-thumb]');
      if (thumb && track) {
        const slide = $$('.gallery__slide', track)[Number(thumb.dataset.thumb)];
        if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
    });

    if (track && thumbs.length) {
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const idx = Math.round(track.scrollLeft / track.clientWidth);
          thumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));
          const active = thumbs[idx];
          if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      }, { passive: true });
    }

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!mixOn() && (!variant || !variant.available)) return;
        errorEl.hidden = true;
        addBtn.classList.add('is-loading');
        try {
          let items;
          if (mixOn()) {
            const counts = new Map();
            mix.forEach((v) => counts.set(v.id, (counts.get(v.id) || 0) + 1));
            items = Array.from(counts, ([id, quantity]) => ({ id, quantity }));
          } else {
            items = [{ id: variant.id, quantity: Math.max(1, Number(qtyInput ? qtyInput.value : 1)) }];
          }
          await Cart.add(items);
          if (!isCartPage) Cart.open();
        } catch (err) {
          errorEl.textContent = err.message;
          errorEl.hidden = false;
        } finally {
          addBtn.classList.remove('is-loading');
        }
      });
    }

    const stickyAdd = $('[data-sticky-add]');
    if (sticky && addBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-on', past);
        sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
      }).observe(addBtn);
      stickyAdd && stickyAdd.addEventListener('click', () => form && form.requestSubmit());
    }

    // Initial paint: start the gallery on the selected color.
    if (variant && variant.featured_media && track) {
      const slides = $$('.gallery__slide', track);
      const idx = slides.findIndex((s) => String(s.dataset.mediaId) === String(variant.featured_media.id));
      if (idx > 0) track.scrollLeft = slides[idx].offsetLeft;
    }
    updateAvailability();
    renderPacks();
    const startPack = $('[data-pack]:checked', root);
    if (startPack) setPack(startPack.value); else renderTotal();
  }

  $$('[data-pdp]').forEach(initProduct);

  /* Product reviews: Show More */
  document.addEventListener('click', (e) => {
    const more = e.target.closest('[data-reviews-more]');
    if (!more) return;
    const list = more.parentElement.querySelector('[data-reviews-list]');
    const hidden = list ? $$('.prev[hidden]', list) : [];
    hidden.slice(0, 6).forEach((li) => { li.hidden = false; });
    if (hidden.length <= 6) more.remove();
  });

  /* ----------------------------------------------------------------------
     Header: sticky border, nav drawer, search
     ---------------------------------------------------------------------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  const navDrawer = $('[data-nav-drawer]');
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-nav-open]')) openLayer(navDrawer, $('[data-nav-close]', navDrawer));
    if (e.target.closest('[data-nav-close]')) closeLayer(navDrawer);

    const searchOpen = e.target.closest('[data-search-open]');
    const bar = $('[data-search-bar]');
    if (searchOpen && bar) {
      e.preventDefault();
      bar.hidden = !bar.hidden;
      if (!bar.hidden) $('input[type=search]', bar).focus();
    }
    if (e.target.closest('[data-search-close]') && bar) bar.hidden = true;

    // Close open filter dropdowns when clicking elsewhere.
    $$('details.fchip[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });

  /* ----------------------------------------------------------------------
     Announcement rotator
     ---------------------------------------------------------------------- */
  $$('[data-rotator]').forEach((wrap) => {
    const items = $$('.announce__msg', wrap);
    if (items.length < 2) return;
    let i = 0;
    setInterval(() => {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, 4500);
  });

  /* ----------------------------------------------------------------------
     Collection filters: apply on change
     ---------------------------------------------------------------------- */
  $$('[data-filters]').forEach((form) => {
    let t;
    const apply = () => {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => { if (value !== '') params.append(key, value); });
      window.location.href = `${form.action}?${params.toString()}`;
    };
    form.addEventListener('change', (e) => {
      if (!e.target.matches('[data-filter-input]')) return;
      clearTimeout(t);
      t = setTimeout(apply, e.target.type === 'number' ? 700 : 0);
    });
    $$('details.fchip', form).forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) $$('details.fchip', form).forEach((o) => { if (o !== d) o.removeAttribute('open'); });
      });
    });
  });

  /* ----------------------------------------------------------------------
     Boot: paint meters from the server-rendered state
     ---------------------------------------------------------------------- */
  Offer.apply(Offer.read(document));
})();
